/**
 * Exposure Service — API client for exposure snapshot endpoints
 *
 * Follows the same pattern as demandService.ts / supplyService.ts
 */

import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// ─── Types ───────────────────────────────────────────────────────────

export interface ExposureSnapshot {
  id: number;
  accountId: number;
  formulaId: number | null;
  formulaCode: string | null;
  formulaName: string | null;
  snapshotDate: string;
  demandQuantityMt: number;
  onHandQuantityMt: number;
  poQuantityMt: number;
  hedgeQuantityMt: number;
  netQuantityMt: number;
  demandValueBase: number;
  onHandValueBase: number;
  poValueBase: number;
  hedgeValueBase: number;
  netExposureBase: number;
  hedgesAreDerived: boolean;
  hedgePriceRatio: number | null;
  spotPrice: number | null;
  spotPriceCurrency: string | null;
  fxRateToBase: number | null;
  baseCurrency: string;
  horizonMonths: number;
  createdAt: string;
}

export interface GetExposureHistoryParams {
  accountId?: number;
  formulaCode?: string;   // formula code or 'GENERAL' for overall
  from?: string;          // YYYY-MM-DD
  to?: string;            // YYYY-MM-DD
  limit?: number;
}

// ─── Auth helpers (same as other services) ───────────────────────────

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;

  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      resolve(null);
    }, timeoutMs);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) throw new Error('Not authenticated');

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ─── API Methods ─────────────────────────────────────────────────────

/**
 * Fetch exposure history snapshots.
 * Pass formulaCode='GENERAL' (or omit) for overall exposure.
 */
export async function getExposureHistory(
  params: GetExposureHistoryParams = {},
): Promise<ExposureSnapshot[]> {
  const headers = await getAuthHeaders();
  const qp = new URLSearchParams();

  if (params.accountId) qp.append('accountId', params.accountId.toString());
  if (params.formulaCode) qp.append('formulaCode', params.formulaCode);
  if (params.from) qp.append('from', params.from);
  if (params.to) qp.append('to', params.to);
  if (params.limit) qp.append('limit', params.limit.toString());

  const url = `${API_URL}/api/v2/exposure/history?${qp.toString()}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch exposure history');
  }

  const data = await response.json();
  return data.snapshots || [];
}

/**
 * Fetch latest exposure snapshot per formula + overall.
 */
export async function getLatestExposure(
  accountId?: number,
): Promise<ExposureSnapshot[]> {
  const headers = await getAuthHeaders();
  const qp = new URLSearchParams();
  if (accountId) qp.append('accountId', accountId.toString());

  const url = `${API_URL}/api/v2/exposure/latest?${qp.toString()}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch latest exposure');
  }

  const data = await response.json();
  return data.snapshots || [];
}

/**
 * Trigger manual exposure recalculation for current account.
 */
export async function triggerExposureCalculation(
  accountId?: number,
): Promise<{ snapshotDate: string; formulaCount: number; rowsUpserted: number }> {
  const headers = await getAuthHeaders();
  const qp = new URLSearchParams();
  if (accountId) qp.append('accountId', accountId.toString());

  const url = `${API_URL}/api/v2/exposure/calculate?${qp.toString()}`;
  const response = await fetch(url, { method: 'POST', headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to trigger exposure calculation');
  }

  return response.json();
}
